import { ref, type Ref } from 'vue';
import type { StreamRequest } from '@/stores/Media/types';
import { MaintenanceApiHandler, type MediaStream, isSessionExpired, type ErrorResponseData  } from "@ca.vmedia.tv/domains"
import { useMedia } from "@/stores/Media";
import { useReLogin } from '../useReLogin';
import { useApplicationStore } from "@/stores/Application";
import { storeToRefs } from 'pinia';
import { useResources } from "@/composables/useResources";
import type { AxiosError } from 'axios';

interface ExposeInterface {
    fetchStream: (streamRequest: StreamRequest) => Promise<MediaStream | null>;
    retryFetchStream: (streamRequest: StreamRequest) => Promise<MediaStream | null>;
    isRetrying: Ref<boolean>;
    fetchStreamErrorHandling: (error: AxiosError | unknown, callback: () => Promise<void> | void) => void;
    error507Handling: (callback: () => void) => Promise<void>;
}

export const useFetchStream = (): ExposeInterface => {
    const ApplicationStoreModule = useApplicationStore();
    const ApplicationStore = useApplicationStore();
    const {
        showGlobalMaintenanceModal,
        setModal507Message,
        setIsShown507ErrorModal,
    } = ApplicationStore;
    const { getIsInternetModalShow } = storeToRefs(ApplicationStoreModule);
    
    const MAX_RETRIES = 3;
    const attempt: Ref<number> = ref(0);
    const isRetrying: Ref<boolean> = ref(false);

    const {
        fetchStream: fetchStreamStore,
    } = useMedia();

    const { reLogin } = useReLogin();

    const fetchStream = async (streamRequest: StreamRequest): Promise<MediaStream | null> => {
        const stream: MediaStream | null = await fetchStreamStore(streamRequest);
        if (!stream || !stream.url) {
            throw new Error("Stream URL is null");
        }
        return stream;
    };

    const retryFetchStream = async (streamRequest: StreamRequest): Promise<MediaStream | null> => {
        while (attempt.value < MAX_RETRIES) {
            try {
                isRetrying.value = true;
                const stream = await fetchStream(streamRequest);
                attempt.value = 0;
                return stream;
            } catch (err) {
                const axiosError = err as AxiosError<ErrorResponseData>;
                if (MaintenanceApiHandler.isMaintenanceError(axiosError)) {
                    showGlobalMaintenanceModal(true);
                    break;
                }
                if (getIsInternetModalShow.value) {
                    console.error('No internet connection');
                    // Add an `online` event handler
                    window.addEventListener('online', function handleOnline() {
                        window.removeEventListener('online', handleOnline);
                        void retryFetchStream(streamRequest); // Retry after internet connection is restored
                    });
                    break; // Stop trying if there is no internet connection
                }
                attempt.value++;
                console.error(`Attempt ${attempt.value} failed:`, {
                    message: axiosError.message,
                    status: axiosError.response?.status,
                    code: axiosError.code
                });
                
                if (attempt.value >= MAX_RETRIES) {
                    attempt.value = 0;
                    throw new Error(axiosError.message);
                }
                if (isSessionExpired(axiosError)) {
                    console.warn('Session expired. Attempting to relogin...');
                    await reLogin();
                    continue; // Try again after successful re-login
                }
            } finally {
                isRetrying.value = false;
            }
        }
        return null;
    };

    const fetchStreamErrorHandling = (error: AxiosError | unknown, callback: () => void): void => {
        if (error && MaintenanceApiHandler.isMaintenanceError(error as AxiosError)) {
            showGlobalMaintenanceModal(true);
            return;
        }
        console.error('Unexpected error:', error);
        callback();
    };

    const error507Handling = async (callback: () => void): Promise<void> => {
        try {
            const message: string = await useResources('message-for-507');
            setModal507Message(message);
            setIsShown507ErrorModal(true);
        } catch (err) {
            console.error('Failed to fetch message for 507 error:', err);
            callback();
        };
    };

    return {
        fetchStream,
        retryFetchStream,
        isRetrying,
        fetchStreamErrorHandling,
        error507Handling,
    }
};
